import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  GetById,
  Update,
  getAllLanguage
} from "adminApi/CommonSettingApi/CommonSetting";
import Spinner from "Common/Globle/Spinner";
import fields from "./json/CommonSettingFields.json";
import useFormChange from "Hooks/useFormChange";
import useFormSubmit from "Hooks/useFormSubmit";
import { validateForm } from "utils/validation";
import { CommonSettingRules } from "validation/ValidationRules";
import CommonSettingForm from "./Componate/CommonSettingForm";

/**
 * CommonSetting Component
 * Manages global site configurations including branding, localization, and storage providers.
 */
const CommonSetting = () => {
  const [loading, setLoading] = useState(false);
  const formdata = useMemo(
    () => [...fields.formdata, ...fields.country_code],
    []
  );
  const id = 1; // Default global settings record ID

  // Initialize form state
  const initialValues = useMemo(() =>
    formdata.reduce((acc, field) => ({ ...acc, [field.name]: "" }), {}),
    [formdata]
  );

  const { values, errors, handleChange, handleFileChange, setErrors, setValues } =
    useFormChange(initialValues, CommonSettingRules);

  /**
   * Data Fetching Logic
   * Retrieves and flattens system settings into the form state.
   */
  const fetchData = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await GetById(id);
      if (res?.success && res?.data) {
        const data = res.data;
        const fetchedValues = {};

        // Map root level fields
        formdata.forEach((field) => {
          fetchedValues[field.name] = data[field.name] ?? "";
        });

        // Map nested storage configurations
        if (data.local) fetchedValues["uploadPath"] = data.local.uploadPath ?? "";
        if (data.cloudinary) {
          fetchedValues["cloudName"] = data.cloudinary.cloudName ?? "";
          fetchedValues["apiKey"] = data.cloudinary.apiKey ?? "";
          fetchedValues["apiSecret"] = data.cloudinary.apiSecret ?? "";
        }
        if (data.aws) {
          fetchedValues["bucket"] = data.aws.bucket ?? "";
          fetchedValues["region"] = data.aws.region ?? "";
          fetchedValues["accessKeyId"] = data.aws.accessKeyId ?? "";
          fetchedValues["secretAccessKey"] = data.aws.secretAccessKey ?? "";
        }

        setValues(fetchedValues);
      }
    } catch (err) {
      console.error("Failed to load common settings:", err);
    } finally {
      setLoading(false);
    }
  }, [id, formdata, setValues]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handle auxiliary data requirements (Languages)
  const [languageOptions, setLanguageOptions] = useState([]);

  useEffect(() => {
    const fetchAuxiliaryData = async () => {
      try {
        const langRes = await getAllLanguage();
        if (langRes?.data?.success) setLanguageOptions(langRes.data.data || []);
      } catch (err) {
        console.error("Failed to load auxiliary data:", err);
      }
    };
    fetchAuxiliaryData();
  }, []);

  /**
   * Form Submission Logic
   * Prepares multi-part form data for server-side processing.
   */
  const { loading: loadingSubmit, handleSubmit } = useFormSubmit(
    async (formValues) => {
      const formData = new FormData();
      Object.entries(formValues).forEach(([key, val]) => {
        // Append all valid fields; File extraction logic is handled in validation phase deletion
        if (val !== null && val !== "" && !key.endsWith("Preview")) {
          formData.append(key, val);
        }
      });
      return await Update({ id, data: formData });
    },
    (vals) => {
      // 🛡️ Pre-validation Sanitization: Remove existing logo URLs so we only send actual new Files
      if (typeof vals.admin_logo === "string") delete vals.admin_logo;
      if (typeof vals.web_logo === "string") delete vals.web_logo;
      
      const err = validateForm(vals, CommonSettingRules);
      setErrors(err);
      return err;
    },
    () => fetchData(),
    "System settings updated successfully."
  );

  return (
    <>
      {(loading || loadingSubmit) && <Spinner isSpinner={true} />}

      <CommonSettingForm
        fields={formdata}
        values={values}
        errors={errors}
        handleChange={handleChange}
        handleFileChange={handleFileChange}
        handleSubmit={(e) => handleSubmit(e, values)}
        loading={loadingSubmit}
        LanguageOptions={languageOptions}
        admin_logoPreview={values.admin_logoPreview || values.admin_logo}
        web_logoPreview={values.web_logoPreview || values.web_logo}
      />
    </>
  );
};

export default CommonSetting;
